import { NextPage } from 'next';
import React from 'react';
import {
  Button, ButtonColor, FontWeight, Grid, Spacing, SpacingName, Typography, Image, Symbol, ICON_MAP_MARKER,
} from '@doveit/lego';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';
import {
  BackgroundImage, CTA, TextAlign, WrapperSize,
} from '../client/types';
import PageSection from '../client/components/page-section/PageSection';
import useRefInView from '../client/hooks/useRefInView/useRefInView';
import scrollToRef from '../client/utils/scroll-to-ref/scrollToRef';
import Hero from '../client/sections/hero/Hero';
import { requireFromStaticAsset, requireResourcesFromStaticAssets } from '../client/utils/assetsUtils';
import { AppNavigationVariant } from '../client/templates/common/app-navigation/types';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import BannerStripe from '../client/components/banner-stripe/BannerStripe';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { ALL_JOB_OFFERS_BY_DEPARTMENT_QUERY, JOB_POSITION_QUERY } from '../client/graphql/queries/job-offer';
import { Department, JobOffer, JobPosition } from '../domain/graphcms';
import CardStack from '../client/components/card-stack/CardStack';
import ArticleMarkdown from '../client/components/article-markdown/ArticleMarkdown';
import * as styles from '../client/templates/work-with-us/WorkWithUs.style';
import { JobApplicationSource } from '../domain/jobApplicationSource';
import SaveJobApplication from '../client/containers/save-job-application/SaveJobApplication';
import {
  BECOME_AGENT_INFO_URL, WORK_WITH_US_INFO_AS, buildURLObject, THANK_YOU_URL, JOB_APPLICATION_THANK_YOU_AS,
} from '../client/utils/links';
import { JobApplication } from '../services/dtos';
import { JOB_APPLICATION_STORAGE_KEY } from './become-agent';
import { employmentTypeLabel } from '../client/constants';
import { ThankYouPageVariant } from '../services/thank-you/getThankYouData';
import { sendJobApplicationSubmitEvent } from '../client/utils/tracking';

export interface WorkWithUsPageProps {
  SEO: PageHeadProps['SEO'],
  hero: {
    title: string,
    description: string,
    cover: BackgroundImage,
    cta: CTA,
  },
  intro: {
    title: string,
    description: string,
  },
  team: {
    title: string,
    description: string,
    image: {
      url: string,
      alt: string,
    },
    cta: CTA,
  },
  quote: {
    text: string,
    author: string,
    image: {
      url: string,
      alt: string,
    },
  },
  jobOffers: {
    title: string,
    list: JobOffer[],
  },
  spontaneousApplication: {
    title: string,
    description: string,
    jobPosition: JobPosition,
  },
}

export const WorkWithUs: NextPage<WorkWithUsPageProps> = ({
  SEO,
  hero,
  intro,
  team,
  quote,
  jobOffers,
  spontaneousApplication,
}) => {
  const { current: formCurrentRef, ref: formRef } = useRefInView({ threshold: 0 });
  const { current: jobOffertsCurrentRef, ref: jobOffersRef } = useRefInView({ threshold: 0 });

  const goToJobApplicationThankYouPage = React.useCallback((jobPosition: JobPosition) => (jobApplication: JobApplication) => {
    sendJobApplicationSubmitEvent(
      'main',
      jobApplication,
      jobPosition,
      {
        urlObject: buildURLObject(
          THANK_YOU_URL,
          JOB_APPLICATION_THANK_YOU_AS,
          { variant: ThankYouPageVariant.JOB_APPLICATION },
        ),
        replace: true,
      },
    );
  }, []);

  const goToJobApplicationInfoStep = React.useCallback((jobPosition: JobPosition) => (jobApplication: JobApplication) => {
    sessionStorage.setItem(JOB_APPLICATION_STORAGE_KEY, JSON.stringify(jobApplication));

    sendJobApplicationSubmitEvent(
      'main',
      jobApplication,
      jobPosition,
      {
        urlObject: {
          url: BECOME_AGENT_INFO_URL,
          as: WORK_WITH_US_INFO_AS,
        },
      },
    );
  }, []);

  return (
    <AppLayout navigationVariant={AppNavigationVariant.LIGHT}>
      <PageHead SEO={SEO} />

      {/** Hero */}
      <Hero
        cover={requireResourcesFromStaticAssets(hero.cover)}
        coverPosition="left center"
      >
        <Typography.H1 color="neutrals.lightest">
          {hero.title}
        </Typography.H1>
        <Spacing margin={[SpacingName.SMALLER, 0, SpacingName.MEDIUM]}>
          <Typography.H3
            color="neutrals.lightest"
            tagName="p"
            fontWeight={FontWeight.LIGHT}
          >
            {hero.description}
          </Typography.H3>
        </Spacing>
        <Button
          {...hero.cta}
          color={ButtonColor.LIGHT}
          onClick={scrollToRef(jobOffertsCurrentRef)}
        />
      </Hero>

      {/** Intro */}
      <PageSection
        padding={[SpacingName.BIGGER, 0, 0]}
        aria-label="Intro"
      >
        <PageSection.Wrapper>
          <Typography.H2>
            {intro.title}
          </Typography.H2>
          <Spacing margin={[SpacingName.SMALL, 0, 0]}>
            <Typography.H4
              tagName="p"
              fontWeight={FontWeight.LIGHT}
            >
              {intro.description}
            </Typography.H4>
          </Spacing>
        </PageSection.Wrapper>
      </PageSection>

      {/** Team */}
      <PageSection
        padding={[SpacingName.BIGGER, 0]}
        aria-label="Team"
      >
        <PageSection.Wrapper>
          <Grid
            addCSS={styles.teamGrid}
            gutter={SpacingName.MEDIUM}
          >
            <Grid.Unit size={{ LG: 1 / 2 }}>
              <Typography.H3
                tagName="p"
                color="brand.primary"
                fontWeight={FontWeight.LIGHT}
              >
                {team.title}
              </Typography.H3>
              <Spacing margin={[SpacingName.SMALL, 0]}>
                <Typography.H4
                  tagName="p"
                  fontWeight={FontWeight.LIGHT}
                >
                  {team.description}
                </Typography.H4>
              </Spacing>
              <Button
                {...team.cta}
                color={ButtonColor.PRIMARY}
                onClick={scrollToRef(formCurrentRef)}
              />
            </Grid.Unit>
            <Grid.Unit size={{ LG: 1 / 2 }}>
              <Image
                src={requireFromStaticAsset(team.image.url)}
                alt={team.image.alt}
                lazyLoad
              />
            </Grid.Unit>
          </Grid>
        </PageSection.Wrapper>
      </PageSection>

      {/** Quote */}
      <BannerStripe
        imageURL={requireFromStaticAsset(quote.image.url)}
        imageAlt={quote.image.alt}
      >
        <Typography.H3
          tagName="p"
          fontWeight={FontWeight.LIGHT}
        >
          {quote.text}
        </Typography.H3>
        <Spacing margin={[SpacingName.SMALL, 0, 0]}>
          <Typography.BODY
            tagName="p"
            fontWeight={FontWeight.LIGHT}
          >
            {quote.author}
          </Typography.BODY>
        </Spacing>
      </BannerStripe>

      {/** Job offers section */}
      <div ref={jobOffersRef}>
        <PageSection padding={[SpacingName.BIGGER, 0]}>
          <PageSection.Wrapper>
            <PageSection.Title textAlign={TextAlign.CENTER}>
              {jobOffers.title}
            </PageSection.Title>
            <div css={styles.jobOffersListWrapper}>
              <CardStack aria-label="Lista delle posizioni aperte">
                {jobOffers.list.map((jobOffer) => (
                  <CardStack.Item
                    title={jobOffer.jobPosition.name}
                    subtitle={(
                      <div css={styles.jobOfferSubtitle}>
                        <Symbol
                          path={ICON_MAP_MARKER}
                          size={20}
                        />
                        {[jobOffer.province?.name || 'Tutta Italia', employmentTypeLabel[jobOffer.employmentType]].join(' - ')}
                      </div>
                    )}
                    description={(
                      <ArticleMarkdown>
                        {jobOffer.abstract}
                      </ArticleMarkdown>
                    )}
                    key={jobOffer.id}
                  >
                    <ArticleMarkdown>
                      {jobOffer.description}
                    </ArticleMarkdown>
                    <Spacing margin={[SpacingName.MEDIUM, 0]}>
                      <Typography.H3 tagName="div">
                        Fai di Dove.it la tua prossima casa
                      </Typography.H3>
                      <Typography.BODY>
                        Invia la tua candidatura ed entra nel team di Dove.it
                      </Typography.BODY>
                    </Spacing>
                    <SaveJobApplication
                      jobPositionId={jobOffer.jobPosition.id}
                      source={JobApplicationSource.LAVORA_CON_NOI}
                      showProvince
                      submitColor={ButtonColor.PRIMARY}
                      onSuccess={goToJobApplicationInfoStep(jobOffer.jobPosition)}
                    />
                  </CardStack.Item>
                ))}
              </CardStack>
            </div>
          </PageSection.Wrapper>
        </PageSection>
      </div>

      {/** Modulo di contatto */}
      <div ref={formRef}>
        <PageSection
          padding={[0, 0, SpacingName.BIGGEST]}
          aria-label="Modulo di contatto"
        >
          <PageSection.Wrapper>
            <PageSection.Title textAlign={TextAlign.CENTER}>
              {spontaneousApplication.title}
            </PageSection.Title>
          </PageSection.Wrapper>

          <PageSection.Wrapper maxWidth={WrapperSize.M}>
            <Spacing margin={[SpacingName.MEDIUM, 0, 0]}>
              <Typography.H4
                tagName="p"
                fontWeight={FontWeight.LIGHT}
              >
                {spontaneousApplication.description}
              </Typography.H4>
            </Spacing>
            <PageSection.Block>
              <SaveJobApplication
                jobPositionId={spontaneousApplication.jobPosition.id}
                dataTracking="work-with-us-form-main-submit"
                submitColor={ButtonColor.PRIMARY}
                onSuccess={goToJobApplicationThankYouPage(spontaneousApplication.jobPosition)}
              />
            </PageSection.Block>
          </PageSection.Wrapper>
        </PageSection>
      </div>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<WorkWithUsPageProps>(async () => {
  const graphQLClient = initGraphQLClient();

  const pageData = await importDynamicData('pages/work-with-us.yml');

  const [{ data: { jobOffers } }, { data: { jobPosition } }] = await Promise.all([
    graphQLClient.query({
      query: ALL_JOB_OFFERS_BY_DEPARTMENT_QUERY,
      variables: {
        departments: [Department.SALES],
      },
    }),
    graphQLClient.query<{ jobPosition: JobPosition }>({
      query: JOB_POSITION_QUERY,
      variables: {
        id: pageData.spontaneousApplication.jobPositionId,
      },
    }),
  ]);

  return {
    props: {
      ...pageData,
      jobOffers: {
        ...pageData.jobOffers,
        list: jobOffers,
      },
      spontaneousApplication: {
        ...pageData.spontaneousApplication,
        jobPosition,
      },
    },
  };
});

export default withError(WorkWithUs);
